<template>
  <b-overlay :show="saving" rounded spinner spinner-variant="primary">
    <div class="topbar mb-2">
      <div>
        <div class="eyebrow">Radioproteção · Levantamento</div>
        <h1>Levantamento (vue)</h1>
      </div>
    </div>

    <div class="status-strip mb-2">
      <span :class="['status-dot', statusClass]"></span>
      <span class="status-text">{{ statusText }}</span>
      <span class="sep"></span>
      <span class="status-meta">KTP {{ fmt(ktp,4) }} · t beam on {{ fmt(tBeam,3) }} h/sem</span>
    </div>

    <b-tabs v-model="tabIndex" pills>
      <b-tab title="1 — Identificação">
        <b-card class="mt-2">
          <b-row>
            <b-col cols="6"><b-form-group label="Acelerador"><b-form-input v-model="ident.acelerador" /></b-form-group></b-col>
            <b-col cols="6"><b-form-group label="Unidade"><b-form-input v-model="ident.unidade" /></b-form-group></b-col>
            <b-col cols="6"><b-form-group label="Monitor"><b-form-input v-model="ident.monitor" /></b-form-group></b-col>
            <b-col cols="6"><b-form-group label="Modelo"><b-form-input v-model="ident.modelo" /></b-form-group></b-col>
            <b-col cols="6"><b-form-group label="Certificado"><b-form-input v-model="ident.certificado" /></b-form-group></b-col>
            <b-col cols="6"><b-form-group label="Data"><b-form-input type="date" v-model="ident.data" /></b-form-group></b-col>
          </b-row>
        </b-card>
      </b-tab>

      <b-tab title="2 — Condições ambientais">
        <b-card class="mt-2">
          <div class="readings-scroll">
            <table class="readings-table mb-2">
              <thead>
                <tr><th>Grandeza</th><th>1</th><th>2</th><th>3</th><th>4</th><th>5</th><th>Média</th><th>Referência</th><th>Unidade</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td class="rowlabel">Temperatura</td>
                  <td v-for="i in 5" :key="i"><input type="number" step="any" v-model.number="reads.temp[i-1]" @input="recalc" /></td>
                  <td class="calc">{{ fmt(tAvg,2) }}</td>
                  <td><input type="number" step="any" v-model.number="reads.tRef" @input="recalc" /></td>
                  <td>°C</td>
                </tr>
                <tr>
                  <td class="rowlabel">Hora</td>
                  <td v-for="i in 5" :key="'t'+i"><input type="time" v-model="reads.tempTime[i-1]" /></td>
                  <td class="calc"></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td class="rowlabel">Pressão</td>
                  <td v-for="i in 5" :key="'p'+i"><input type="number" step="any" v-model.number="reads.press[i-1]" @input="recalc" /></td>
                  <td class="calc">{{ fmt(pAvg,2) }}</td>
                  <td><input type="number" step="any" v-model.number="reads.pRef" @input="recalc" /></td>
                  <td>mbar</td>
                </tr>
                <tr>
                  <td class="rowlabel">Hora</td>
                  <td v-for="i in 5" :key="'pt'+i"><input type="time" v-model="reads.pressTime[i-1]" /></td>
                  <td class="calc"></td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-row>
            <b-col cols="6"><b-form-group label="KTP"><b-form-input readonly :value="fmt(ktp,5)" /></b-form-group></b-col>
            <b-col cols="6"><b-form-group label="KE"><b-form-input type="number" step="any" v-model.number="ke" @input="recalc" /></b-form-group></b-col>
          </b-row>
        </b-card>
      </b-tab>

      <b-tab title="3 — Trabalho e limites">
        <b-card class="mt-2">
          <b-row>
            <b-col cols="4"><b-form-group label="Dias úteis / semana"><b-form-input type="number" v-model.number="diasUteis" @input="recalc" /></b-form-group></b-col>
            <b-col cols="4"><b-form-group label="Unidade de leitura do monitor"><b-form-select v-model="unidMonitor" :options="['mSv/h','mR/h']" @input="recalc" /></b-form-group></b-col>
          </b-row>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label="Carga de trabalho (Gy/sem)"><b-form-input type="number" v-model.number="carga" @input="recalc" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convCarga" :options="['Gy/sem','cGy/sem','mGy/sem','Gy/ano','Gy/dia']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(cargaConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label="Carga de trabalho IMRT (Gy/sem)"><b-form-input type="number" v-model.number="cargaImrt" @input="recalc" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convCargaImrt" :options="['Gy/sem','cGy/sem','mGy/sem','Gy/ano','Gy/dia']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(cargaImrtConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label="Taxa de dose (cGy/min)"><b-form-input type="number" v-model.number="taxaDose" @input="recalc" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convTaxa" :options="['cGy/min','Gy/min','Gy/h','cGy/h','mGy/min']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(taxaConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label='t "beam on" — carga de trabalho (h/sem)'><b-form-input readonly :value="fmt(tBeam, 4)" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convTbeam" :options="['h/sem','min/sem','s/sem','h/dia']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(tBeamConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label='t "beam on" — carga IMRT (h/sem)'><b-form-input readonly :value="fmt(tBeamImrt, 4)" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convTbeamImrt" :options="['h/sem','min/sem','s/sem','h/dia']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(tBeamImrtConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label="Limite de dose — Público (mSv/sem)"><b-form-input type="number" v-model.number="limPublico" @input="recalc" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convLimPublico" :options="['mSv/sem','µSv/sem','mSv/ano','mrem/sem','mR/sem']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(limPublicoConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label="Limite de dose — IOE (mSv/sem)"><b-form-input type="number" v-model.number="limIoe" @input="recalc" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convLimIoe" :options="['mSv/sem','µSv/sem','mSv/ano','mrem/sem','mR/sem']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(limIoeConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>

          <div class="conv-row mt-3">
            <b-row class="align-items-end no-gutters">
              <b-col cols="5"><b-form-group label="Limite de dose — BG / fundo"><b-form-input type="number" v-model.number="limBg" @input="recalc" /></b-form-group></b-col>
              <b-col cols="3"><b-form-group label="Converter para"><b-form-select v-model="convLimBg" :options="['mSv/h','µSv/h','µSv/min','mR/h','µR/h']" @input="recalc" /></b-form-group></b-col>
              <b-col cols="4"><b-form-group label="Valor convertido"><b-form-input readonly :value="fmt(limBgConv, 5)" /></b-form-group></b-col>
            </b-row>
          </div>
        </b-card>
      </b-tab>

      <b-tab title="4 — Pontos">
        <b-card class="mt-2">
          <my-radiometry-table
            :rows.sync="points"
            :cols="pointCols"
            :calc-fn="calcPointLeitura"
            @add="addPoint"
          />
          <div class="mt-2 text-right">
            <b-button class="mr-2" size="sm" variant="primary" @click="recalc">Recalcular</b-button>
            <b-button class="mr-2" size="sm" variant="outline-secondary" @click="saveDraft">Salvar</b-button>
            <b-button size="sm" variant="outline-danger" @click="clearDraft">Limpar</b-button>
          </div>
        </b-card>
      </b-tab>
    </b-tabs>
</b-overlay>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BFormSelect,
  BButton,
  BOverlay,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import MyRadiometryTable from '@/views/components/radio/MyRadiometryTable.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const defaultPointCols = [
  {
    key: 'desc',
    label: 'Descrição',
    type: 'text',
  },
  {
    key: 'pto',
    label: 'Pontos',
    type: 'text',
  },
  {
    key: 'barreira',
    label: 'Barreira',
    type: 'text',
  },
  { key: 'd', label: 'LG0', type: 'number' },
  { key: 'e', label: 'LG90', type: 'number' },
  { key: 'f', label: 'LG180', type: 'number' },
  { key: 'g', label: 'LG270', type: 'number' },
  { key: 'calcH', label: 'Max', type: 'number' },
  { key: 'calcI', label: 'Max-BG', type: 'number' },
  { key: 'u', label: 'U', type: 'text' },
  { key: 't', label: 'T', type: 'text' },
  { key: 'area', label: 'Área', type: 'text' },
  { key: 'viz', label: 'Vizinhança', type: 'text' },
  { key: 'nfontes', label: 'Nº fontes', type: 'number' },
  { key: 'limiteFonte', label: 'Limite/fonte', type: 'number' },
  { key: 'conclusao', label: 'Conclusão', type: 'text' },
]

export default {
  name: 'ProjectUpsertForm',
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BFormSelect,
    BButton,
    BOverlay,
    MyRadiometryTable,
    BTabs,
    BTab,
  },
  data() {
    return {
      saving: false,
      tabIndex: 0,
      ident: {
        acelerador: '',
        unidade: '',
        monitor: '',
        modelo: '',
        certificado: '',
        data: '',
      },
      reads: {
        temp: [null, null, null, null, null],
        tempTime: ['', '', '', '', ''],
        press: [null, null, null, null, null],
        pressTime: ['', '', '', '', ''],
        tRef: 20,
        pRef: 1013.25,
      },
      ke: 1,
      diasUteis: 5,
      unidMonitor: 'mSv/h',
      convCarga: 'Gy/sem',
      convCargaImrt: 'Gy/sem',
      convTaxa: 'cGy/min',
      convTbeam: 'h/sem',
      convTbeamImrt: 'h/sem',
      convLimPublico: 'mSv/sem',
      convLimIoe: 'mSv/sem',
      convLimBg: 'mSv/h',
      carga: 1100,
      cargaImrt: 5500,
      taxaDose: 600,
      limPublico: 0.02,
      limIoe: 0.4,
      limBg: 0.0003,
      points: [],
      pointSeq: 0,
      pointCols: defaultPointCols.map(col => ({ ...col })),
      // base labels used to render unit-aware column headers
      pointBaseLabels: {
        d: 'LG=0°',
        e: 'LG=90°',
        f: 'LG=180°',
        g: 'LG=270°',
        calcH: 'Max',
        calcI: 'Max-BG',
      },
      ktp: null,
      tBeam: null,
      tBeamImrt: null,
      statusText: 'Nenhum ponto avaliado ainda',
      statusClass: '',
    }
  },
  computed: {
    tAvg() {
      return this.avg(this.reads.temp)
    },
    pAvg() {
      return this.avg(this.reads.press)
    },
    cargaConv() {
      return this.convertCarga(this.carga, this.convCarga)
    },
    cargaImrtConv() {
      return this.convertCarga(this.cargaImrt, this.convCargaImrt)
    },
    taxaConv() {
      return this.convertTaxa(this.taxaDose, this.convTaxa)
    },
    tBeamConv() {
      return this.convertTempo(this.tBeam, this.convTbeam)
    },
    tBeamImrtConv() {
      return this.convertTempo(this.tBeamImrt, this.convTbeamImrt)
    },
    limPublicoConv() {
      return this.convertLimite(this.limPublico, this.convLimPublico)
    },
    limIoeConv() {
      return this.convertLimite(this.limIoe, this.convLimIoe)
    },
    limBgConv() {
      return this.convertLimiteBG(this.limBg, this.convLimBg)
    },
  },
  async created() {
    // if route contains an id (not 'new'), load from backend; otherwise restore draft
    const id = this.$route && this.$route.params ? this.$route.params.id : null
    if (id && id !== 'new') {
      await this.loadFromServer(id)
    } else {
      this.loadDraft()
    }
    this.pointCols = this.normalizePointCols(this.pointCols)
    this.recalc()
  },
  methods: {
    convertCarga(baseGySem, target) {
      if (baseGySem === null) return null
      switch (target) {
        case 'Gy/sem': return baseGySem
        case 'cGy/sem': return baseGySem * 100
        case 'mGy/sem': return baseGySem * 1000
        case 'Gy/ano': return baseGySem * 52
        case 'Gy/dia': return this.diasUteis ? baseGySem / this.diasUteis : null
        default: return null
      }
    },

    convertTaxa(baseCgyMin, target) {
      if (baseCgyMin === null) return null
      switch (target) {
        case 'cGy/min': return baseCgyMin
        case 'Gy/min': return baseCgyMin / 100
        case 'Gy/h': return baseCgyMin * 0.6
        case 'cGy/h': return baseCgyMin * 60
        case 'mGy/min': return baseCgyMin * 10
        default: return null
      }
    },

    convertTempo(baseHSem, target) {
      if (baseHSem === null) return null
      switch (target) {
        case 'h/sem': return baseHSem
        case 'min/sem': return baseHSem * 60
        case 's/sem': return baseHSem * 3600
        case 'h/dia': return this.diasUteis ? baseHSem / this.diasUteis : null
        default: return null
      }
    },

    convertLimite(baseMsvSem, target) {
      if (baseMsvSem === null) return null
      switch (target) {
        case 'mSv/sem': return baseMsvSem
        case 'µSv/sem': return baseMsvSem * 1000
        case 'mSv/ano': return baseMsvSem * 52
        case 'mrem/sem': return baseMsvSem * 100
        case 'mR/sem': return baseMsvSem / 0.00876
        default: return null
      }
    },

    convertLimiteBG(baseVal, target) {
      if (baseVal === null) return null
      const msvH = baseVal * (this.unidMonitor === 'mR/h' ? 0.00876 : 1)
      switch (target) {
        case 'mSv/h': return msvH
        case 'µSv/h': return msvH * 1000
        case 'µSv/min': return (msvH * 1000) / 60
        case 'mR/h': return msvH / 0.00876
        case 'µR/h': return (msvH * 1000) / 0.00876
        default: return null
      }
    },

    addPoint() {
      // keep a stable id for Vue list rendering and to mirror the example's defaults
      this.pointSeq += 1
      const newPt = {
        id: this.pointSeq,
        desc: '',
        pto: '',
        barreira: 'Primária',
        d: null,
        e: null,
        f: null,
        g: null,
        u: '',
        t: '',
        area: 'Livre',
        viz: '',
        nfontes: 1,
        // pre-create computed fields to avoid adding reactive keys later
        calcH: null,
        calcI: null,
        leitura: null,
        limiteFonte: null,
        okFlag: null,
        conclusao: null,
      }
      this.points.push(newPt)
      // assign a new array reference so child components reliably observe the change
      this.points = this.points.slice()
      // ensure reactivity and then recalc
      this.$nextTick(() => {
        this.recalc()
      })
    },

    removePoint(i) {
      this.points.splice(i, 1)
      this.points = this.points.slice()
      this.recalc()
    },

    recalc() {
      this.ktp = null
      if (
        this.tAvg !== null
        && this.pAvg !== null
        && this.reads.tRef !== null
        && this.reads.pRef !== null
        && this.pAvg !== 0
      ) {
        this.ktp = ((273.2 + this.tAvg) * this.reads.pRef) / ((273.2 + this.reads.tRef) * this.pAvg)
      }

      if (this.carga !== null && this.taxaDose) {
        const doseFraction = this.taxaDose / 100
        const minutesFactor = 60 * doseFraction
        this.tBeam = this.carga / minutesFactor
      } else {
        this.tBeam = null
      }

      if (this.cargaImrt !== null && this.taxaDose) {
        const doseFractionImrt = this.taxaDose / 100
        const minutesFactorImrt = 60 * doseFractionImrt
        this.tBeamImrt = this.cargaImrt / minutesFactorImrt
      } else {
        this.tBeamImrt = null
      }

      this.pointCols = this.normalizePointCols(this.pointCols)

      // update column labels to include current monitor unit (like the HTML example)
      const unit = this.unidMonitor || ''
      this.pointCols = this.pointCols.map(col => {
        if (['d', 'e', 'f', 'g', 'calcH', 'calcI'].includes(col.key)) {
          const base = this.pointBaseLabels[col.key] || col.label
          return { ...col, label: `${base} (${unit})` }
        }
        return col
      })

      let naoOk = 0
      let evaluated = 0

      for (let idx = 0; idx < this.points.length; idx += 1) {
        const pt = this.points[idx]
        const d = Number(pt.d) || null
        const e = Number(pt.e) || null
        const f = Number(pt.f) || null
        const g = Number(pt.g) || null
        const h = this.maxOrNull([d, e, f, g])
        const iVal = (h !== null && this.limBg !== null) ? h - this.limBg : null

        const j = this.parseFraction(pt.u)
        const k = this.parseFraction(pt.t)
        const nFontes = pt.nfontes ? Number(pt.nfontes) : 1

        let leitura = null
        if (iVal !== null && this.ktp !== null && this.ke !== null && j !== null && k !== null) {
          const baseT = (pt.barreira === 'Primária') ? this.tBeam : this.tBeamImrt
          if (baseT !== null) {
            const unitFactor = (this.unidMonitor === 'mR/h') ? 0.00876 : 1
            leitura = iVal * this.ktp * this.ke * k * j * baseT * unitFactor
          }
        }

        let limiteFonte = null
        if (h !== null) {
          const limiteBase = (pt.area === 'Livre') ? this.limPublico : this.limIoe
          if (limiteBase !== null) {
            limiteFonte = limiteBase / (nFontes || 1)
          }
        }

        // mutate existing point object to avoid replacing it (keeps reactivity cheaper)
        pt.calcH = h
        pt.calcI = iVal
        pt.leitura = leitura
        pt.limiteFonte = limiteFonte

        if (leitura !== null && limiteFonte !== null) {
          evaluated += 1
          const ok = leitura < limiteFonte
          if (!ok) naoOk += 1
          pt.okFlag = ok
          pt.conclusao = ok ? 'Conforme' : 'Acima do limite'
        } else {
          pt.okFlag = null
          pt.conclusao = '–'
        }
      }

      if (evaluated === 0) {
        this.statusText = 'Nenhum ponto avaliado ainda'
        this.statusClass = ''
      } else if (naoOk === 0) {
        this.statusText = `Todos os ${evaluated} pontos avaliados estão conformes`
        this.statusClass = 'ok'
      } else {
        this.statusText = `${naoOk} de ${evaluated} ponto(s) acima do limite`
        this.statusClass = 'warn'
      }
    },

    calcPointLeitura(row) { return row.leitura || 0 },

    async saveDraft() {
      // ensure computed values are up-to-date
      this.recalc()
      const payload = {
        ident: this.ident,
        reads: this.reads,
        diasUteis: this.diasUteis,
        unidMonitor: this.unidMonitor,
        convCarga: this.convCarga,
        convCargaImrt: this.convCargaImrt,
        convTaxa: this.convTaxa,
        convTbeam: this.convTbeam,
        convTbeamImrt: this.convTbeamImrt,
        convLimPublico: this.convLimPublico,
        convLimIoe: this.convLimIoe,
        convLimBg: this.convLimBg,
        carga: this.carga,
        cargaImrt: this.cargaImrt,
        taxaDose: this.taxaDose,
        limPublico: this.limPublico,
        limIoe: this.limIoe,
        limBg: this.limBg,
        ke: this.ke,
        ktp: this.ktp,
        tBeam: this.tBeam,
        tBeamImrt: this.tBeamImrt,
        tAvg: this.tAvg,
        pAvg: this.pAvg,
        pointCols: this.pointCols,
        points: this.points,
      }
      this.saving = true
      try {
        const id = this.$route && this.$route.params ? this.$route.params.id : null
        if (!id || id === 'new') {
          await this.$store.dispatch('project/generateRadioReport', { payload })
        } else {
          await this.$store.dispatch('project/updateRadioReport', { id, payload })
        }
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Rascunho salvo',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$router.push({ name: 'pages-radio' })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            ...this.$t('projectUpsertForm.upsertdErrorToast'),
            icon: 'XIcon',
            variant: 'danger',
          },
        })
        // console.error('Erro ao salvar rascunho:', e)
      } finally {
        this.saving = false
      }
      // localStorage.setItem('levantamento_draft', JSON.stringify(payload))
    },

    loadDraft() {
      const s = localStorage.getItem('levantamento_draft')
      if (!s) return
      try {
        const d = JSON.parse(s)
        this.ident = { ...this.ident, ...(d.ident || {}) }
        this.reads = { ...this.reads, ...(d.reads || {}) }
        this.diasUteis = d.diasUteis || this.diasUteis
        this.unidMonitor = d.unidMonitor || this.unidMonitor
        this.convCarga = d.convCarga || this.convCarga
        this.convCargaImrt = d.convCargaImrt || this.convCargaImrt
        this.convTaxa = d.convTaxa || this.convTaxa
        this.convTbeam = d.convTbeam || this.convTbeam
        this.convTbeamImrt = d.convTbeamImrt || this.convTbeamImrt
        this.convLimPublico = d.convLimPublico || this.convLimPublico
        this.convLimIoe = d.convLimIoe || this.convLimIoe
        this.convLimBg = d.convLimBg || this.convLimBg
        this.carga = d.carga || this.carga
        this.cargaImrt = d.cargaImrt || this.cargaImrt
        this.taxaDose = d.taxaDose || this.taxaDose
        this.limPublico = d.limPublico || this.limPublico
        this.limIoe = d.limIoe || this.limIoe
        this.limBg = d.limBg || this.limBg
        this.ke = d.ke || this.ke
        this.points = d.points || this.points
        this.pointCols = this.normalizePointCols(d.pointCols || this.pointCols)
      } catch (e) {
        // ignore
      }
    },

    async loadFromServer(id) {
      try {
        const rec = await this.$store.dispatch('radio/get', { id })
        if (!rec) return
        // server may return input as string
        const input = rec.input && typeof rec.input === 'string' ? JSON.parse(rec.input) : rec.input
        if (!input || !input.data) return
        const d = input.data
        // shallow-assign known fields
        if (d.ident) this.ident = { ...this.ident, ...d.ident }
        if (d.reads) this.reads = { ...this.reads, ...d.reads }
        this.diasUteis = d.diasUteis != null ? d.diasUteis : this.diasUteis
        this.unidMonitor = d.unidMonitor || this.unidMonitor
        this.convCarga = d.convCarga || this.convCarga
        this.convCargaImrt = d.convCargaImrt || this.convCargaImrt
        this.convTaxa = d.convTaxa || this.convTaxa
        this.convTbeam = d.convTbeam || this.convTbeam
        this.convTbeamImrt = d.convTbeamImrt || this.convTbeamImrt
        this.convLimPublico = d.convLimPublico || this.convLimPublico
        this.convLimIoe = d.convLimIoe || this.convLimIoe
        this.convLimBg = d.convLimBg || this.convLimBg
        this.carga = d.carga != null ? d.carga : this.carga
        this.cargaImrt = d.cargaImrt != null ? d.cargaImrt : this.cargaImrt
        this.taxaDose = d.taxaDose != null ? d.taxaDose : this.taxaDose
        this.limPublico = d.limPublico != null ? d.limPublico : this.limPublico
        this.limIoe = d.limIoe != null ? d.limIoe : this.limIoe
        this.limBg = d.limBg != null ? d.limBg : this.limBg
        this.ke = d.ke != null ? d.ke : this.ke
        this.ktp = d.ktp != null ? d.ktp : this.ktp
        this.tBeam = d.tBeam != null ? d.tBeam : this.tBeam
        this.tBeamImrt = d.tBeamImrt != null ? d.tBeamImrt : this.tBeamImrt
        // cols and points
        this.pointCols = this.normalizePointCols(d.pointCols || this.pointCols)
        if (Array.isArray(d.points) && d.points.length) {
          this.points = d.points.map(p => ({ ...p }))
          // set pointSeq to max existing id to avoid collisions
          const maxId = this.points.reduce((m, p) => Math.max(m, p.id || 0), 0)
          this.pointSeq = maxId
        }
        // ensure computed fields are consistent
        this.$nextTick(() => this.recalc())
      } catch (e) {
        // console.error('Failed to load radio entry', e)
      }
    },

    clearDraft() { localStorage.removeItem('levantamento_draft'); this.points = []; this.recalc() },

    /* helpers */
    normalizePointCols(cols) {
      const known = new Map(defaultPointCols.map(col => [col.key, col]))
      const source = Array.isArray(cols) && cols.length ? cols : defaultPointCols
      const ordered = []
      const seen = new Set()

      defaultPointCols.forEach(col => {
        const match = source.find(item => item && item.key === col.key)
        if (match) {
          ordered.push({ ...col, ...match })
          seen.add(col.key)
        } else if (known.has(col.key)) {
          ordered.push({ ...known.get(col.key) })
        }
      })

      source.forEach(col => {
        if (col && col.key && !seen.has(col.key)) {
          ordered.push({ ...col })
        }
      })

      return ordered
    },

    fmt(v, decimals = 5) {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return '–'
      return parseFloat(Number(v).toFixed(decimals)).toString()
    },

    avg(vals) {
      const filled = (vals || []).filter(val => val !== null && val !== undefined)
      if (!filled.length) return null
      return filled.reduce((a, b) => a + b, 0) / filled.length
    },

    maxOrNull(vals) {
      const filled = (vals || []).filter(val => val !== null && val !== undefined)
      if (!filled.length) return null
      return Math.max(...filled)
    },

    parseFraction(v) {
      if (!v && v !== 0) return null
      const s = String(v).trim()
      if (!s) return null
      const FRACTION_RE = /^\s*-?\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?\s*$/
      if (FRACTION_RE.test(s)) {
        const parts = s.split('/')
        const n = parseFloat(parts[0])
        const d = parseFloat(parts[1])
        if (Number.isNaN(n) || Number.isNaN(d) || d === 0) return null
        return n / d
      }

      const n = parseFloat(s)
      return Number.isNaN(n) ? null : n
    },
  },
}
</script>

<style>
@media (max-width: 767px){ .nav-pills{ display:block } }
.flex-description-row{ display:flex; align-items:center }
.status-strip{ display:flex; align-items:center; gap:12px; padding:8px; background:#fff; border:1px solid #ddd }
.status-dot{ width:10px; height:10px; border-radius:50%; background:#888 }
.status-dot.ok{ background: #2E7D46 }
.status-dot.warn{ background: #B23A2E }
/* readings table sizing and inputs */
.readings-table{ width:100%; table-layout:fixed; border-collapse:collapse }
.readings-table th:first-child,
.readings-table td.rowlabel{ width:20%; }
.readings-table th,
.readings-table td{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; padding:6px }
.readings-table input{ width:100%; box-sizing:border-box; padding:6px }
.readings-scroll{ overflow-x:auto }

.conv-row .row { margin-left: 0; margin-right: 0; }
.conv-row .col-3, .conv-row .col-4, .conv-row .col-5 { padding-left: 8px; padding-right: 8px; }
.conv-row .b-form-group { margin-bottom: 0; }
</style>
